<template>
    <div class="jgile-carousel creative-popper">
        <div class="creative-popper-inner">
            <div class="row switcher">
                <!-- Previous button -->
                <div v-if="total > 1" class="col-2">
                    <button class="btn prev" @click="previous" :disabled="loading">
                        <i class="fal fa-chevron-left fa-fw"></i>
                        <span class="sr-only">Previous Creative</span>
                    </button>
                </div>

                <!-- Creative display -->
                <div :class="total > 1 ? 'col-8' : 'col-12'">
                    <div class="current-item">
                        <div class="current-item-content">
                            <div v-if="loading" class="text-center py-4">
                                <i class="fa fa-spinner fa-spin"></i>
                            </div>
                            <creative-display
                                v-else-if="currentCreative"
                                :creative="currentCreative"
                            />
                        </div>
                    </div>
                </div>

                <!-- Next button -->
                <div v-if="total > 1" class="col-2">
                    <button class="btn next" @click="next" :disabled="loading">
                        <i class="fal fa-chevron-right fa-fw"></i>
                        <span class="sr-only">Next Creative</span>
                    </button>
                </div>
            </div>

            <!-- Footer with creative name -->
            <div class="row popper-footer">
                <div v-if="currentCreative" class="col current-item-description">
                    <template v-if="currentCreative.creative_type !== 'text'">
                        {{ currentCreative.media_name || 'Creative' }}
                    </template>
                    <template v-else>
                        Text/Copy
                        <span v-if="total > 1">({{ currentPage }})</span>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CreativeDisplay from './CreativeDisplay.vue';

export default {
    name: 'CreativeCarousel',

    components: {
        CreativeDisplay,
    },

    props: {
        /**
         * Array of creative objects to display.
         * Each should have: id, creative_type, text, media_url, media_name, description
         */
        creativeItems: {
            type: Array,
            required: true,
            default: () => [],
        },

        /**
         * Initial page/index to display (1-based).
         */
        initialPage: {
            type: Number,
            default: 1,
        },
    },

    data() {
        return {
            currentPage: this.initialPage,
            loading: false,
        };
    },

    computed: {
        /**
         * Total number of creative items.
         */
        total() {
            return this.creativeItems.length;
        },

        /**
         * Current creative item to display (0-indexed from currentPage).
         */
        currentCreative() {
            if (this.total === 0) return null;
            const index = this.currentPage - 1;
            return this.creativeItems[index] || null;
        },
    },

    watch: {
        creativeItems: {
            handler(newItems) {
                // Reset to page 1 if items change and current page is out of bounds
                if (this.currentPage > newItems.length) {
                    this.currentPage = 1;
                }
            },
            immediate: false,
        },
    },

    methods: {
        /**
         * Go to previous creative (wraps around).
         */
        previous() {
            if (this.currentPage === 1) {
                this.currentPage = this.total;
            } else {
                this.currentPage--;
            }
            this.$emit('page-change', this.currentPage);
        },

        /**
         * Go to next creative (wraps around).
         */
        next() {
            if (this.currentPage === this.total) {
                this.currentPage = 1;
            } else {
                this.currentPage++;
            }
            this.$emit('page-change', this.currentPage);
        },

        /**
         * Go to a specific page.
         */
        goToPage(page) {
            if (page >= 1 && page <= this.total) {
                this.currentPage = page;
                this.$emit('page-change', this.currentPage);
            }
        },
    },
};
</script>

<style scoped>
.creative-popper {
    width: 100%;
}

.creative-popper-inner {
    padding: 10px;
}

.switcher {
    align-items: center;
    min-height: 200px;
}

.current-item {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 180px;
}

.current-item-content {
    width: 100%;
    text-align: center;
}

.btn.prev,
.btn.next {
    padding: 10px 15px;
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.btn.prev:hover:not(:disabled),
.btn.next:hover:not(:disabled) {
    background-color: #f5f5f5;
}

.btn.prev:disabled,
.btn.next:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.popper-footer {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #eee;
}

.current-item-description {
    text-align: center;
    font-size: 0.9em;
    color: #666;
}
</style>
